import {StateCreator} from "zustand/vanilla";
import {
  adminCompaniesAPI,
  adminConfigAPI,
  adminTierAPI,
  adminTiersAPI,
  adminUsersAPI,
  adminUsersRolesAPI
} from "@/app/apiPaths";
import {UsageTier} from "@/pb/api/devplan/types/usage";
import {
  AdminUserWithRoles,
  DeleteTierResponse,
  GetGlobalConfigResponse,
  GetTiersResponse,
  ListAllCompaniesResponse,
  ListAllRolesResponse,
  ListAllUsersResponse,
  RoleEntry,
  UpdateGlobalConfigRequest,
  UpdateGlobalConfigResponse,
  UpsertTierRequest,
  UpsertTierResponse
} from "@/pb/api/devplan/services/web/admin";
import {GlobalConfig} from "@/pb/api/devplan/types/config";
import {CompanyDetails} from "@/pb/api/devplan/types/company";

export type AdminStoreState = {
  tiers: Record<string, UsageTier>;
  fetchTiers(): Promise<string[]>;
  upsertTier(tier: UsageTier): Promise<string>;
  deleteTier(id: string): Promise<string>;

  globalConfig: GlobalConfig | undefined;
  fetchGlobalConfig(): Promise<void>;
  updateGlobalConfig(config: GlobalConfig): Promise<void>;

  adminUsers: AdminUserWithRoles[];
  fetchAdminUsers(): Promise<void>;

  adminRoles: {globalRoles: RoleEntry[]; companyRoles: RoleEntry[]} | undefined;
  fetchAdminRoles(): Promise<void>;

  adminCompanies: CompanyDetails[];
  fetchAdminCompanies(): Promise<void>;
}

export const createAdminSlice: StateCreator<
  AdminStoreState,
  [],
  [],
  AdminStoreState
> = ((set) => ({
  tiers: {},
  fetchTiers: () => fetch(adminTiersAPI())
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {tiers} = GetTiersResponse.fromJSON(js)
      const newTiers = tiers.reduce((a, t) => ({...a, [t.id]: t}), {} as Record<string, UsageTier>)
      set(s => ({...s, tiers: newTiers}))
      return tiers.map(t => t.id)
    }),
  deleteTier: id => fetch(adminTierAPI(id), {method: "DELETE"})
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {tiers} = DeleteTierResponse.fromJSON(js)
      const newTiers = tiers.reduce((a, t) => ({...a, [t.id]: t}), {} as Record<string, UsageTier>)
      set(s => ({...s, tiers: newTiers}))
      return id
    })
  ,
  upsertTier: tier => fetch(adminTiersAPI(), {method: "POST", body: JSON.stringify(UpsertTierRequest.toJSON({tier}))})
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {tier: updated} = UpsertTierResponse.fromJSON(js)
      set(s => ({...s, tiers: {...s.tiers, [updated!.id]: updated!}}))
      return updated!.id
    }),

  globalConfig: undefined,
  fetchGlobalConfig: async () => fetch(adminConfigAPI())
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => set(s => ({...s, globalConfig: GetGlobalConfigResponse.fromJSON(js).config})))
  ,
  updateGlobalConfig: async (config) => fetch(adminConfigAPI(),
    {method: "POST", body: JSON.stringify(UpdateGlobalConfigRequest.toJSON({config}))})
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => set(s => ({...s, globalConfig: UpdateGlobalConfigResponse.fromJSON(js).config}))),

  adminUsers: [],
  fetchAdminUsers: async () => fetch(adminUsersAPI())
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {users} = ListAllUsersResponse.fromJSON(js)
      set(s => ({...s, adminUsers: users}))
    }),
  adminRoles: undefined,
  fetchAdminRoles: async () => fetch(adminUsersRolesAPI())
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const roles = ListAllRolesResponse.fromJSON(js)
      set(s => ({...s, adminRoles: roles}))
    }),

  adminCompanies: [],
  fetchAdminCompanies: async () => fetch(adminCompaniesAPI())
    .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
    .then(js => {
      const {companies} = ListAllCompaniesResponse.fromJSON(js)
      set(s => ({...s, adminCompanies: companies}))
    }),
}))
